/**
 * **Affixes** (PRD #795): the second axis, independent of the material ladder.
 *
 * An affix is a declaration of what it can roll and how it contributes, and
 * nothing else. It plugs into the equipment stage of the derivation fold that
 * PRD #792 already built, which is the reason this PRD adds content and a
 * roller rather than a second stat pipeline: a rolled affix becomes an
 * {@link ../../combat/derivedStats.EquipmentContribution}, and combat code
 * never learns that items exist.
 *
 * **Tier bands gate by drop position, not by pool.** Depth does not add affix
 * kinds; it unlocks higher tiers of the ones that already exist. That is what
 * keeps the vocabulary a player learns once true at every depth, and it is why
 * an affix declares five bands rather than five sibling affixes.
 *
 * **Names are the classic vocabulary** named in #713: a weapon runs from Ruin
 * to Vanquishing and armour from Defense to Invulnerability, so the tier a
 * player reads is a word they already know rather than a number.
 *
 * Every band here is **first-pass** and owned by the tuning PRD (#799).
 */

import type { DamageType } from '../../combat/abilityCatalog';
import type { CombatAttributes } from '../../combat/derivedStats';
import { ARPG_ITEM_SLOTS, type ArpgItemSlot } from './bases';

/** How many tiers every affix declares. Depth widens the reachable band. */
export const ARPG_AFFIX_TIERS = 5;

/** Item levels between one affix tier and the next. */
export const ARPG_AFFIX_TIER_STEP = 3;

/** Where a rolled affix value lands in the derivation fold. */
export type ArpgAffixContribution =
  | { channel: 'attribute'; key: keyof CombatAttributes }
  | {
      channel: 'flat';
      key:
        | 'maxHealth'
        | 'maxMana'
        | 'healthRegenPerTick'
        | 'manaRegenPerTick'
        | 'mainHandDamage'
        | 'critChance';
    }
  | { channel: 'resist'; damageType: DamageType }
  | {
      channel: 'percent';
      key:
        | 'maxHealth'
        | 'maxMana'
        | 'physicalDamage'
        | 'spellDamage'
        | 'attackSpeed'
        | 'castSpeed';
    };

/** One rollable band: the values this tier of this affix can produce. */
export interface ArpgAffixBand {
  min: number;
  max: number;
}

export interface ArpgAffixDefinition {
  id: string;
  /** Whether the affix reads before the base name or after it. */
  kind: 'prefix' | 'suffix';
  /**
   * The word per tier, shallowest first. A line with one word for every tier
   * keeps its own name and lets the value carry the tier.
   */
  names: readonly string[];
  /** Slots this affix may roll on. */
  slots: ReadonlySet<ArpgItemSlot>;
  /** Selection weight within the eligible pool. Higher is commoner. */
  weight: number;
  /** Where the rolled value enters the fold. */
  contribution: ArpgAffixContribution;
  /** One band per tier, shallowest first. */
  bands: readonly ArpgAffixBand[];
  /** Decimal places a rolled value keeps. Zero for the whole numbers most affixes roll. */
  precision?: number;
  /** How the value reads on a tooltip, after the value itself. */
  unit?: '%' | '';
}

const ARMOR_SLOTS: readonly ArpgItemSlot[] = [
  'head',
  'chest',
  'arms',
  'hands',
  'legs',
  'feet',
  'offHand',
];

const JEWELRY_SLOTS: readonly ArpgItemSlot[] = ['neck', 'ring'];

const EVERY_SLOT: readonly ArpgItemSlot[] = ARPG_ITEM_SLOTS;

function bands(values: readonly [number, number][]): ArpgAffixBand[] {
  return values.map(([min, max]) => ({ min, max }));
}

const AFFIX_DEFINITIONS: readonly ArpgAffixDefinition[] = [
  // Prefixes.
  {
    id: 'weapon-damage',
    kind: 'prefix',
    names: ['Ruin', 'Might', 'Force', 'Power', 'Vanquishing'],
    slots: new Set<ArpgItemSlot>(['mainHand']),
    weight: 10,
    contribution: { channel: 'flat', key: 'mainHandDamage' },
    bands: bands([
      [1, 3],
      [3, 6],
      [6, 10],
      [10, 15],
      [15, 22],
    ]),
  },
  {
    id: 'weapon-accuracy',
    kind: 'prefix',
    names: [
      'Accurate',
      'Surpassingly Accurate',
      'Eminently Accurate',
      'Exceedingly Accurate',
      'Supremely Accurate',
    ],
    slots: new Set<ArpgItemSlot>(['mainHand']),
    weight: 6,
    contribution: { channel: 'flat', key: 'critChance' },
    bands: bands([
      [0.01, 0.02],
      [0.02, 0.035],
      [0.035, 0.05],
      [0.05, 0.07],
      [0.07, 0.1],
    ]),
    precision: 3,
  },
  {
    id: 'hale',
    kind: 'prefix',
    names: ['Hale', 'Robust', 'Stalwart', 'Ironbound', 'Adamant'],
    slots: new Set<ArpgItemSlot>([...ARMOR_SLOTS, ...JEWELRY_SLOTS]),
    weight: 10,
    contribution: { channel: 'flat', key: 'maxHealth' },
    bands: bands([
      [5, 12],
      [12, 22],
      [22, 35],
      [35, 52],
      [52, 75],
    ]),
  },
  {
    id: 'swift',
    kind: 'prefix',
    names: ['Swift', 'Fleet', 'Quick', 'Rapid', 'Blinding'],
    slots: new Set<ArpgItemSlot>(EVERY_SLOT),
    weight: 5,
    contribution: { channel: 'percent', key: 'attackSpeed' },
    bands: bands([
      [1, 3],
      [3, 5],
      [5, 8],
      [8, 11],
      [11, 15],
    ]),
    unit: '%',
  },
  {
    id: 'arcane',
    kind: 'prefix',
    names: ['Arcane', 'Mystic', 'Occult', 'Eldritch', 'Transcendent'],
    slots: new Set<ArpgItemSlot>(EVERY_SLOT),
    weight: 5,
    contribution: { channel: 'percent', key: 'spellDamage' },
    bands: bands([
      [2, 5],
      [5, 9],
      [9, 14],
      [14, 20],
      [20, 28],
    ]),
    unit: '%',
  },

  // Suffixes.
  {
    id: 'defense',
    kind: 'suffix',
    names: [
      'Defense',
      'Guarding',
      'Hardening',
      'Fortification',
      'Invulnerability',
    ],
    slots: new Set<ArpgItemSlot>(ARMOR_SLOTS),
    weight: 10,
    contribution: { channel: 'resist', damageType: 'physical' },
    bands: bands([
      [1, 2],
      [2, 4],
      [4, 6],
      [6, 9],
      [9, 12],
    ]),
    unit: '%',
  },
  ...attributeAffix('strength', 'Strength'),
  ...attributeAffix('dexterity', 'Dexterity'),
  ...attributeAffix('intelligence', 'Intelligence'),
  ...attributeAffix('vitality', 'Vitality'),
  ...attributeAffix('wisdom', 'Wisdom'),
  ...elementalAffix('fire', 'the Phoenix'),
  ...elementalAffix('cold', 'the Glacier'),
  ...elementalAffix('poison', 'the Serpent'),
  ...elementalAffix('energy', 'the Storm'),
  {
    id: 'mana-pool',
    kind: 'suffix',
    names: ['Mana', 'Mana', 'Mana', 'Mana', 'Mana'],
    slots: new Set<ArpgItemSlot>([...ARMOR_SLOTS, ...JEWELRY_SLOTS]),
    weight: 6,
    contribution: { channel: 'flat', key: 'maxMana' },
    bands: bands([
      [4, 8],
      [8, 14],
      [14, 22],
      [22, 32],
      [32, 45],
    ]),
  },
  {
    id: 'regeneration',
    kind: 'suffix',
    names: [
      'Regeneration',
      'Regeneration',
      'Regeneration',
      'Regeneration',
      'Regeneration',
    ],
    slots: new Set<ArpgItemSlot>([...ARMOR_SLOTS, ...JEWELRY_SLOTS]),
    weight: 4,
    contribution: { channel: 'flat', key: 'healthRegenPerTick' },
    bands: bands([
      [0.01, 0.03],
      [0.03, 0.06],
      [0.06, 0.1],
      [0.1, 0.15],
      [0.15, 0.22],
    ]),
    precision: 3,
  },
  {
    id: 'focus',
    kind: 'suffix',
    names: ['Focus', 'Focus', 'Focus', 'Focus', 'Focus'],
    slots: new Set<ArpgItemSlot>([...ARMOR_SLOTS, ...JEWELRY_SLOTS]),
    weight: 4,
    contribution: { channel: 'flat', key: 'manaRegenPerTick' },
    bands: bands([
      [0.01, 0.03],
      [0.03, 0.06],
      [0.06, 0.1],
      [0.1, 0.15],
      [0.15, 0.22],
    ]),
    precision: 3,
  },
];

/**
 * One attribute line. Every attribute rolls the same band, because an attribute
 * point is an attribute point: what makes them different is what the fold does
 * with them, and that is the fold's job rather than an affix's.
 */
function attributeAffix(
  key: keyof CombatAttributes,
  name: string,
): ArpgAffixDefinition[] {
  return [
    {
      id: `attribute-${key}`,
      kind: 'suffix',
      names: [name, name, name, name, name],
      slots: new Set<ArpgItemSlot>([...ARMOR_SLOTS, ...JEWELRY_SLOTS]),
      weight: 8,
      contribution: { channel: 'attribute', key },
      bands: bands([
        [1, 3],
        [3, 6],
        [6, 10],
        [10, 15],
        [15, 21],
      ]),
    },
  ];
}

/** One elemental resist line, named for the thing it turns away. */
function elementalAffix(
  damageType: DamageType,
  name: string,
): ArpgAffixDefinition[] {
  return [
    {
      id: `resist-${damageType}`,
      kind: 'suffix',
      names: [name, name, name, name, name],
      slots: new Set<ArpgItemSlot>([...ARMOR_SLOTS, ...JEWELRY_SLOTS]),
      weight: 6,
      contribution: { channel: 'resist', damageType },
      bands: bands([
        [2, 4],
        [4, 7],
        [7, 11],
        [11, 16],
        [16, 22],
      ]),
      unit: '%',
    },
  ];
}

const BY_ID = new Map<string, ArpgAffixDefinition>(
  AFFIX_DEFINITIONS.map((definition) => [definition.id, definition]),
);

export const ARPG_AFFIXES: readonly ArpgAffixDefinition[] = AFFIX_DEFINITIONS;

export function arpgAffix(affixId: string): ArpgAffixDefinition | null {
  return BY_ID.get(affixId) ?? null;
}

/**
 * The highest affix tier a drop at this position can reach.
 *
 * Depth is the gate: an item level of one reaches tier one, and every three
 * levels past it opens the next band. An elite reaches one band deeper than the
 * floor it stands on, which is the encounter PRD's promise that the danger pays
 * (PRD #794), expressed as a widened pool rather than as a bonus roll.
 */
export function maxAffixTierFor(itemLevel: number, elite = false): number {
  const level = Number.isFinite(itemLevel) ? Math.max(1, Math.floor(itemLevel)) : 1;
  const reached = 1 + Math.floor((level - 1) / ARPG_AFFIX_TIER_STEP);
  return Math.min(ARPG_AFFIX_TIERS, Math.max(1, reached + (elite ? 1 : 0)));
}

/** The word this affix wears at this tier. */
export function affixName(
  definition: ArpgAffixDefinition,
  tier: number,
): string {
  const index = Math.min(definition.names.length, Math.max(1, tier)) - 1;
  return definition.names[index] ?? definition.names[0]!;
}

/** The band this affix rolls in at this tier. */
export function affixBand(
  definition: ArpgAffixDefinition,
  tier: number,
): ArpgAffixBand {
  const index = Math.min(definition.bands.length, Math.max(1, tier)) - 1;
  return definition.bands[index] ?? definition.bands[0]!;
}

/** Every affix that may roll on this slot. */
export function affixesForSlot(slot: ArpgItemSlot): ArpgAffixDefinition[] {
  return AFFIX_DEFINITIONS.filter((definition) => definition.slots.has(slot));
}
